import React from 'react';
import styled, { keyframes } from 'styled-components';
import { FaTools, FaWrench, FaClock, FaDollarSign, FaPlus } from 'react-icons/fa';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EstimateCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transform: scale(0.9);

  @media (max-width: 480px) {
    padding: 1rem;
    transform: scale(0.95);
  }
`;

const ItemDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const Price = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: #1f2937;
`;

const LineItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
  transition: transform 0.2s ease;
  animation: ${fadeInUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: ${props => props.index * 0.1}s;
  opacity: 0;
  
  &:hover {
    background: #f8fafc;
    transform: translateY(-1px);
  }

  @media (max-width: 480px) {
    padding: 0.75rem;
    gap: 0.5rem;
    flex-wrap: wrap;
    
    ${ItemDetails} {
      width: 100%;
    }
    
    ${Price} {
      width: 100%;
      text-align: right;
      margin-top: 0.5rem;
    }
  }
`;

const IconWrapper = styled.div`
  width: 40px;
  height: 40px;
  background: #f1f5f9;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  svg {
    color: #6366f1;
    font-size: 1.4rem;
    transition: transform 0.2s ease;
  }

  ${LineItem}:hover & {
    background: #e0e7ff;
    
    svg {
      transform: scale(1.1);
    }
  }
`;

const ItemText = styled.div`
  h4 {
    font-size: 1.1rem;
    margin: 0;
    color: #1f2937;
    font-weight: 500;
  }
  
  p {
    font-size: 0.95rem;
    color: #6b7280;
    margin: 0.25rem 0 0 0;
  }

  @media (max-width: 768px) {
    h4 {
      font-size: 0.9rem;
    }
    
    p {
      font-size: 0.8rem;
    }
  }
`;

const Total = styled(LineItem)`
  margin-top: 2rem;
  border-top: 2px solid #e5e7eb;
  background: #f8fafc;
  padding: 1.75rem;
  box-shadow: none;
  
  ${ItemText} h4 {
    font-size: 1.2rem;
    font-weight: 600;
  }
  
  ${Price} {
    font-size: 1.5rem;
    color: #6366f1;
    font-weight: 700;
  }

  ${IconWrapper} {
    background: #e0e7ff;
    
    svg {
      color: #6366f1;
    }
  }

  &:hover {
    transform: none;
    background: #f8fafc;
  }
`;

const AddItemButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1.25rem;
  background: #f8fafc;
  border: 2px dashed #e2e8f0;
  border-radius: 16px;
  color: #64748b;
  width: 100%;
  justify-content: center;
  cursor: pointer;
  margin: 2rem 0;
  transition: all 0.2s ease;
  font-size: 1rem;
  font-weight: 500;
  animation: ${fadeInUp} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  animation-delay: 0.3s;
  opacity: 0;
  
  &:hover {
    background: #f1f5f9;
    color: #6366f1;
    border-color: #6366f1;
    transform: translateY(-1px);
  }
  
  svg {
    font-size: 1rem;
    transition: transform 0.2s ease;
  }

  &:hover svg {
    transform: rotate(90deg);
  }
`;

const EstimateHeader = styled.div`
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
  animation: ${fadeInDown} 0.5s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  
  h3 {
    color: #1f2937;
    margin: 0 0 0.75rem 0;
    font-size: 1.75rem;
    font-weight: 600;
  }
  
  p {
    color: #6b7280;
    margin: 0;
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  text-align: center;

  .value {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .label {
    color: #6b7280;
    font-size: 0.875rem;
  }
`;

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const EstimateDemo = () => {
  const items = [
    {
      icon: <FaTools />,
      name: "Service Call",
      description: "Initial Diagnostic",
      price: "$89.00"
    },
    {
      icon: <FaWrench />,
      name: "Parts & Labor",
      description: "Repair Components",
      price: "$245.00"
    },
    {
      icon: <FaClock />,
      name: "Emergency Fee",
      description: "After Hours Service",
      price: "$75.00"
    }
  ];

  const total = items.reduce((sum, item) => 
    sum + parseFloat(item.price.replace('$', '')), 0);

  return (
    <Container>
      <DotLottieReact
        src="https://lottie.host/36165734-6929-4c35-b4a8-98a4a4ab0014/tt9ipA1qNy.lottie"
        loop
        autoplay
      />
    </Container>
  );
};

export default EstimateDemo;